// NEW_FILE_JUSTIFIED: New page for Retell AI chat session history — no existing equivalent for retellChatSessions. Generic helper names (filterReducer, formatDate) are page-local and not exported.
/**
 * ChatSessionHistoryPage
 *
 * Filtered, paginated table of Retell AI chat sessions.
 * Server-side filters: sentiment, success, leadStatus, search (via usePaginatedQuery).
 */

import { useReducer, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useChatSessionHistory } from '@/hooks/useRetellChats';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MessageSquare, Clock, User, DollarSign } from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  IChatHistoryFilterState,
  TChatHistoryFilterAction,
  TChatSentiment,
  IRetellChatSession,
} from '@/@types/retellChat';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ require three levels up to reach convex/_generated/

// --- Constants ---

const CHAT_SENTIMENTS: TChatSentiment[] = ['Positive', 'Neutral', 'Negative', 'Unknown'];

// --- Filter Reducer ---

const INITIAL_FILTER_STATE: IChatHistoryFilterState = {
  search: '',
  sentiment: 'all',
  success: 'all',
  leadStatus: 'all',
};

function filterReducer(
  state: IChatHistoryFilterState,
  action: TChatHistoryFilterAction
): IChatHistoryFilterState {
  switch (action.type) {
    case 'SET_SEARCH':
      return { ...state, search: action.payload };
    case 'SET_SENTIMENT':
      return { ...state, sentiment: action.payload };
    case 'SET_SUCCESS':
      return { ...state, success: action.payload };
    case 'SET_LEAD_STATUS':
      return { ...state, leadStatus: action.payload };
  }
}

// --- Type Guard Helpers ---

function toSentiment(value: string): TChatSentiment | undefined {
  return CHAT_SENTIMENTS.find(s => s === value);
}

// --- Format Helpers ---

function formatDate(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${String(sec).padStart(2, '0')}`;
}

function formatCost(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

// --- Sentiment Badge Config ---

const SENTIMENT_CLASSES: Record<TChatSentiment, string> = {
  Positive: 'bg-green-500/10 text-green-600 border-green-200',
  Neutral: 'bg-muted text-muted-foreground border-border',
  Negative: 'bg-destructive/10 text-destructive border-destructive/20',
  Unknown: 'bg-muted text-muted-foreground border-border',
};

// --- Row Sub-component ---

function ChatSessionRow({
  session,
  onClick,
}: {
  session: IRetellChatSession;
  onClick: (id: Id<'retellChatSessions'>) => void;
}) {
  return (
    <TableRow
      className={cn('cursor-pointer hover:bg-muted/20')}
      onClick={() => onClick(session._id)}
    >
      <TableCell className="text-sm">{formatDate(session.startedAt)}</TableCell>
      <TableCell className="text-sm">
        <span className="inline-flex items-center gap-1">
          <Clock className="w-3 h-3 text-muted-foreground" />
          {session.durationMs !== undefined ? formatDuration(session.durationMs) : '—'}
        </span>
      </TableCell>
      <TableCell className="text-sm">
        <span className="inline-flex items-center gap-1">
          <User className="w-3 h-3 text-muted-foreground" />
          {session.visitorName ?? 'Anonymous'}
        </span>
      </TableCell>
      <TableCell>
        {session.sentiment !== undefined ? (
          <Badge variant="outline" className={cn(SENTIMENT_CLASSES[session.sentiment])}>
            {session.sentiment}
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell>
        {session.chatSuccessful === true ? (
          <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-200">
            Success
          </Badge>
        ) : session.chatSuccessful === false ? (
          <Badge
            variant="outline"
            className="bg-destructive/10 text-destructive border-destructive/20"
          >
            Unsuccessful
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
        {session.chatSummary ?? '—'}
      </TableCell>
      <TableCell className="text-sm">
        <span className="inline-flex items-center gap-1">
          <DollarSign className="w-3 h-3 text-muted-foreground" />
          {session.costCents !== undefined ? formatCost(session.costCents) : '—'}
        </span>
      </TableCell>
      <TableCell>
        {session.leadId !== undefined ? (
          <Badge variant="outline" className="bg-violet-500/10 text-violet-600 border-violet-200">
            Linked
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">No lead</span>
        )}
      </TableCell>
    </TableRow>
  );
}

// --- Page Component ---

export function ChatSessionHistoryPage() {
  // 1. Hooks
  const navigate = useNavigate();
  const { currentOrg, isLoading: orgLoading } = useOrgContext();
  const [filters, dispatch] = useReducer(filterReducer, INITIAL_FILTER_STATE);

  // 2. Computed / memoised
  const serverFilters = useMemo(
    () => ({
      sentiment: toSentiment(filters.sentiment),
      success: filters.success !== 'all' ? (filters.success as 'yes' | 'no') : undefined,
      leadStatus:
        filters.leadStatus !== 'all' ? (filters.leadStatus as 'has_lead' | 'no_lead') : undefined,
      search: filters.search.trim() || undefined,
    }),
    [filters]
  );

  const {
    results,
    isLoading: sessionsLoading,
    loadMore,
    status,
  } = useChatSessionHistory(currentOrg?._id, serverFilters);

  const isLoading = orgLoading || sessionsLoading;

  const handleRowClick = useCallback(
    (sessionId: Id<'retellChatSessions'>) => {
      navigate(`/insurance/chat-agents/${sessionId}`);
    },
    [navigate]
  );

  const handleLoadMore = useCallback(() => loadMore(25), [loadMore]);

  // 3. Guard returns
  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <MessageSquare className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to view chat history.
          </p>
        </div>
      </div>
    );
  }

  // 4. Primary return
  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground">Chat History</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          {isLoading ? 'Loading...' : `${results.length} session${results.length !== 1 ? 's' : ''}`}
        </p>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input
          placeholder="Search by name, summary, or transcript..."
          value={filters.search}
          onChange={e => dispatch({ type: 'SET_SEARCH', payload: e.target.value })}
          className="max-w-xs"
        />
        <Select
          value={filters.sentiment}
          onValueChange={v => {
            const sent = toSentiment(v);
            dispatch({ type: 'SET_SENTIMENT', payload: sent ?? 'all' });
          }}
        >
          <SelectTrigger className={cn('w-36')}>
            <SelectValue placeholder="Sentiment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sentiment</SelectItem>
            <SelectItem value="Positive">Positive</SelectItem>
            <SelectItem value="Neutral">Neutral</SelectItem>
            <SelectItem value="Negative">Negative</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filters.success}
          onValueChange={v => dispatch({ type: 'SET_SUCCESS', payload: v as 'all' | 'yes' | 'no' })}
        >
          <SelectTrigger className={cn('w-36')}>
            <SelectValue placeholder="Success" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="yes">Successful</SelectItem>
            <SelectItem value="no">Unsuccessful</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filters.leadStatus}
          onValueChange={v =>
            dispatch({
              type: 'SET_LEAD_STATUS',
              payload: v as 'all' | 'has_lead' | 'no_lead',
            })
          }
        >
          <SelectTrigger className={cn('w-36')}>
            <SelectValue placeholder="Lead" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Leads</SelectItem>
            <SelectItem value="has_lead">Has Lead</SelectItem>
            <SelectItem value="no_lead">No Lead</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-table-container border border-table-container-border rounded-none overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-36">Date/Time</TableHead>
                <TableHead className="w-24">Duration</TableHead>
                <TableHead>Visitor</TableHead>
                <TableHead className="w-28">Sentiment</TableHead>
                <TableHead className="w-32">Success</TableHead>
                <TableHead>Summary</TableHead>
                <TableHead className="w-24">Cost</TableHead>
                <TableHead className="w-24">Lead</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-16 text-muted-foreground">
                    Loading sessions...
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && results.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-16">
                    <MessageSquare className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">
                      No chat sessions match your filters
                    </p>
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                results.map((session: IRetellChatSession) => (
                  <ChatSessionRow key={session._id} session={session} onClick={handleRowClick} />
                ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Load More */}
      {status === 'CanLoadMore' && (
        <div className="flex justify-center mt-4">
          <Button variant="outline" onClick={handleLoadMore}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}
